// src/components/sections/Projects.jsx
import React from "react";
import { ArrowUpRightIcon, LineChartIcon, BrainIcon, BarChartIcon } from "../../icons/Icons";
import MiniChart from "../visuals/MiniChart";
import HeaderMono from "../ui/HeaderMono";
import { projects, earlierProjects } from "../../content";

const ICONS = {
  brain: <BrainIcon className="w-10 h-10 text-emerald-300" />,
  line: <LineChartIcon className="w-10 h-10 text-cyan-300" />,
  bar: <BarChartIcon className="w-10 h-10 text-cyan-300" />,
};

function ProjectLinks({ repo, demo, demoLabel = "Live Demo" }) {
  if (!repo && !demo) return null;
  return (
    <div className="flex items-center gap-3">
      {repo && (
        <a href={repo} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 font-medium">
          Repo <ArrowUpRightIcon className="w-4 h-4" />
        </a>
      )}
      {demo && (
        <a href={demo} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 font-medium">
          {demoLabel} <ArrowUpRightIcon className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

export default function Projects() {
  return (
    <section id="projects" className="border-t border-white/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between gap-4 mb-6">
          <HeaderMono className="mb-0">Projects</HeaderMono>
          <a href="#contact" className="text-sm text-cyan-300 hover:text-cyan-200 transition">
            Hire me →
          </a>
        </div>

        {/* Featured work */}
        <div className="space-y-6">
          {projects.map((p, idx) => (
            <article
              key={idx}
              className="group rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition overflow-hidden shadow-sm hover:shadow-emerald-500/10"
            >
              <div className="p-5 md:p-6 grid md:grid-cols-[1fr_260px] gap-6 items-start">
                <div className="min-w-0">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0" aria-hidden="true">{ICONS[p.icon]}</div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-lg leading-snug flex flex-wrap items-center gap-2">
                        {p.title}
                        {p.tag && (
                          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 border border-white/10 rounded-full px-2 py-0.5">
                            {p.tag}
                          </span>
                        )}
                      </h3>
                      <p className="mt-2 text-sm text-slate-300 leading-relaxed">{p.blurb}</p>
                    </div>
                  </div>
                  <ul className="mt-4 flex flex-wrap gap-2 text-[11px] text-slate-300/90 font-mono">
                    {p.stack.map((t) => (
                      <li key={t} className="px-2.5 py-1 rounded-full bg-slate-900/60 border border-white/10">{t}</li>
                    ))}
                  </ul>
                  <div className="mt-4">
                    <ProjectLinks repo={p.repo} demo={p.demo} demoLabel={p.demoLabel} />
                  </div>
                </div>
                <div className="hidden md:block" aria-hidden="true">
                  <MiniChart type={p.viz.type} data={p.viz.data} />
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Earlier work — collapsed by default */}
        <details className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
          <summary className="cursor-pointer font-mono text-sm text-slate-300 hover:text-cyan-300 transition">
            Earlier Projects
          </summary>
          <ul className="mt-4 space-y-3">
            {earlierProjects.map((p) => (
              <li key={p.title} className="text-sm text-slate-300">
                <span className="font-semibold">{p.title}</span>
                {" — "}
                {p.blurb}
                {(p.repo || p.demo) && (
                  <span className="ml-2 inline-flex gap-3 align-middle">
                    {p.repo && (
                      <a href={p.repo} target="_blank" rel="noreferrer noopener" className="text-cyan-300 hover:text-cyan-200">Repo</a>
                    )}
                    {p.demo && (
                      <a href={p.demo} target="_blank" rel="noreferrer noopener" className="text-emerald-300 hover:text-emerald-200">Demo</a>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </details>
      </div>
    </section>
  );
}
